import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { ClothingItem, ClothingType } from "../types/wardrobe";
import { api } from "../services/api";
import axios, { AxiosError } from "axios";
import WardrobeItem from "./WardrobeItem";
import { useResponsive } from "../hooks/useResponsive";

interface WardrobeListProps {
  items: ClothingItem[];
  onItemDeleted?: (itemId: string) => void;
  onItemUpdate?: (updatedItems: ClothingItem[]) => void;
  isDemoMode?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wardrobe-tabpanel-${index}`}
      aria-labelledby={`wardrobe-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const WardrobeList: React.FC<WardrobeListProps> = ({
  items,
  onItemDeleted,
  onItemUpdate,
  isDemoMode = false,
}) => {
  const { isMobile } = useResponsive();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ClothingItem | null>(null);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState(0);

  const handleDeleteClick = (item: ClothingItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await api.deleteClothingItem(itemToDelete._id);
      setSuccess("Item deleted successfully");
      onItemDeleted?.(itemToDelete._id);
    } catch (err) {
      const error = err as Error | AxiosError;
      setError(
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : "Failed to delete item"
      );
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setError("");
    setSuccess("");
  };

  const handleItemUpdate = (updatedItem: ClothingItem) => {
    const updatedItems = items.map((item) =>
      item._id === updatedItem._id ? updatedItem : item
    );
    onItemUpdate?.(updatedItems);
    setSuccess("Item updated successfully");
  };

  // Group items by type (category) and sort by name for consistency
  const getItemsByType = (
    items: ClothingItem[]
  ): Record<ClothingType, ClothingItem[]> => {
    const grouped: Record<ClothingType, ClothingItem[]> = {
      top: [],
      bottom: [],
      dress: [],
      outerwear: [],
      shoes: [],
      accessory: [],
    };

    items.forEach((item) => {
      if (grouped[item.type]) {
        grouped[item.type].push(item);
      }
    });

    // Sort items within each category by name for consistent display
    Object.keys(grouped).forEach((type) => {
      grouped[type as ClothingType].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    });

    return grouped;
  };

  const groupedItems = getItemsByType(items);
  const categoryTabs = Object.entries(groupedItems).map(
    ([type, typeItems]) => ({
      type,
      count: typeItems.length,
      items: typeItems,
    })
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const renderCategoryContent = (categoryItems: ClothingItem[]) => {
    if (categoryItems.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No items in this category yet.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={isMobile ? 1 : 3}>
        {categoryItems.map((item) => (
          <Grid item key={item._id} xs={12} sm={6} md={4} lg={3}>
            <WardrobeItem
              item={item}
              onUpdate={handleItemUpdate}
              onDelete={handleDeleteClick}
              isDemoMode={isDemoMode}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: isMobile ? 1 : 3,
        backgroundColor: "#F4EDE4",
        minHeight: "100vh",
      }}
    >
      {/* Category Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "#C49A88",
          mb: 2,
          backgroundColor: "#FFFCF7",
          borderRadius: 2,
          p: isMobile ? 0.5 : 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="wardrobe category tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              color: "#6B7B8C",
              fontWeight: 500,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              minWidth: isMobile ? "auto" : "auto",
              px: isMobile ? 1 : 2,
              "&.Mui-selected": {
                color: "#1A1A1A",
              },
              "&:hover": {
                color: "#C49A88",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#C49A88",
            },
          }}
        >
          {categoryTabs.map((category, index) => (
            <Tab
              key={category.type}
              label={`${
                category.type.charAt(0).toUpperCase() + category.type.slice(1)
              } (${category.count})`}
              id={`wardrobe-tab-${index}`}
              aria-controls={`wardrobe-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Panels */}
      {categoryTabs.map((category, index) => (
        <TabPanel key={category.type} value={selectedTab} index={index}>
          {renderCategoryContent(category.items)}
        </TabPanel>
      ))}

      {items.length === 0 && (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            py: 4,
            backgroundColor: "#FFFCF7",
            borderRadius: 2,
            border: "1px solid #C49A88",
          }}
        >
          <Typography variant="h6" color="#6B7B8C">
            No items in your wardrobe yet. Start by adding some clothes!
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            backgroundColor: "#FFFCF7",
            border: "1px solid #C49A88",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: "#1A1A1A" }}>Delete Item</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#6B7B8C" }}>
            Are you sure you want to delete {itemToDelete?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: "#6B7B8C" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{
              color: "#FFFCF7",
              backgroundColor: "#1A1A1A",
              "&:hover": {
                backgroundColor: "#404040",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : "success"}
          sx={{
            width: "100%",
            backgroundColor: error ? "#FFE6E6" : "#E6F4EA",
            color: "#1A1A1A",
            border: `1px solid ${error ? "#FF6B6B" : "#AABBA5"}`,
          }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};
